import { queryOptions } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { shouldRetry } from '../api';

import { fetchServerSession } from './api/session.api';
import { fetchViewablePeople } from './api/viewAs.api';
import { knowsDetails } from './describedPerson';
import { DETAILS_STALE, SESSION_STALE, sessionKeys, viewAsKeys } from './query-keys';

/** The session as the server describes it. */
export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: sessionKeys.current(),
    queryFn: ({ signal }) => fetchServerSession(signal),
    staleTime: SESSION_STALE,
    // The session is the tab's for as long as the tab is open, watched or not.
    gcTime: Infinity,
    retry: (failureCount: number, error: unknown) => failureCount < 1 && shouldRetry(failureCount, error),
  });

/**
 * The same read, for screens that show what the server keeps about the person: made again at once
 * while this tab has only sign-in's word for who is signed in, and after half a minute otherwise.
 */
export const personDetailsQueryOptions = () =>
  queryOptions({
    ...sessionQueryOptions(),
    staleTime: (query) => (knowsDetails(query.state.data) ? DETAILS_STALE : 0),
  });

/** The signed-in person's id from the session already read, for code that runs outside components. */
export const currentUserIdOf = (queryClient: QueryClient): string | null => {
  const session = queryClient.getQueryData(sessionQueryOptions().queryKey);
  return session?.status === 'signedIn' ? session.user.id : null;
};

export const viewablePeopleQueryOptions = (search: string) =>
  queryOptions({
    queryKey: viewAsKeys.people(search),
    queryFn: ({ signal }) => fetchViewablePeople(search, signal),
    staleTime: 30_000,
  });
